import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMediaQuery } from 'react-responsive';
import AllListItem from './AllListItem';
import styles from './List.module.scss';
import ListItem from './ListItem';
import Search from './Search';
import { TagOption } from './TagOption';

interface ListProps<T> {
    triggerRef?: React.RefObject<HTMLDivElement>;
    searchable?: boolean;
    searchPlaceholder?: string;
    tags: Array<TagOption<T>>;
    selectAll?: boolean;
    selectAllLabel?: string;
    selectAllIcon?: IconDefinition;
    direction?: 'up' | 'down';
    className?: string;
    onClose?: () => void;
    onSelect?: (id: T) => void;
    onSelectAll?: (ids: Array<T>) => void;
}

export const List = <T,>(props: ListProps<T>) => {
    const listRef = useRef<HTMLDivElement>(null);

    const [searchText, setSearchText] = useState('');
    const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

    const isMobile = useMediaQuery({ query: '(max-width:600px)' });

    const scrollStyle = isMobile ? { height: '100%' } : { flex: 1, maxHeight: '40vh' };

    const listTags = useMemo(
        () =>
            searchText === ''
                ? [...props.tags]
                : props.tags.filter(tag => {
                      const tokens = tag.name.split(' ').map(token => token.toLowerCase());
                      return tokens.some(
                          token => tag.name.toLowerCase().startsWith(searchText) || token.startsWith(searchText)
                      );
                  }),
        [props.tags, searchText]
    );

    const selectAllHandler = () => {
        props.onSelectAll?.(listTags.map(tag => tag.id));
    };

    const calculatePosition = useCallback(() => {
        if (props.triggerRef?.current && listRef.current) {
            const sideOffset = 8;
            const alignOffset = 0;
            const collisionPadding = 10;

            const triggerRect = props.triggerRef.current.getBoundingClientRect();
            const contentRect = listRef.current.getBoundingClientRect();

            console.log(triggerRect, contentRect);

            // Cross-browser viewport dimensions
            const viewportWidth = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
            const viewportHeight = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);

            const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
            const availableWidth = viewportWidth - scrollbarWidth;

            let top = 0;
            let left = 0;

            switch (props.direction) {
                case 'up':
                    top = triggerRect.top - contentRect.height - sideOffset;
                    break;
                case 'down':
                default:
                    top = triggerRect.bottom + sideOffset;
                    break;
            }

            left = triggerRect.left + alignOffset;

            // Horizontal overflow handling
            const rightEdge = left + contentRect.width;
            let adjustedLeft = left;

            if (rightEdge > availableWidth - collisionPadding) {
                adjustedLeft = triggerRect.right - contentRect.width;
                if (adjustedLeft < collisionPadding) {
                    adjustedLeft = availableWidth - contentRect.width - collisionPadding;
                }
            }
            adjustedLeft = Math.max(collisionPadding, adjustedLeft);

            // Vertical overflow handling
            const bottomEdge = top + contentRect.height;
            let adjustedTop = top;

            if (bottomEdge > viewportHeight - collisionPadding) {
                if (props.direction === 'down' || !props.direction) {
                    adjustedTop = triggerRect.top - contentRect.height - sideOffset;
                }
                if (adjustedTop < collisionPadding) {
                    adjustedTop = viewportHeight - contentRect.height - collisionPadding;
                }
            }
            adjustedTop = Math.max(collisionPadding, adjustedTop);

            setPosition({ top: adjustedTop, left: adjustedLeft });
        }
    }, [props.direction]);

    useEffect(() => {
        calculatePosition();
    }, [calculatePosition]);

    useEffect(() => {
        const handleScrollOrResize = () => {
            calculatePosition();
        };

        window.addEventListener('scroll', handleScrollOrResize, true); // Use capture for all scroll events
        window.addEventListener('resize', handleScrollOrResize);

        return () => {
            window.removeEventListener('scroll', handleScrollOrResize, true);
            window.removeEventListener('resize', handleScrollOrResize);
        };
    }, [calculatePosition]);

    return createPortal(
        <div
            className={clsx(styles.container, { [styles.up]: props.direction === 'up' }, props.className)}
            style={{
                top: position?.top,
                left: position?.left,
                ...(!position && {
                    visibility: 'hidden'
                })
            }}
        >
            <button
                type="button"
                className={styles.closeButton}
                onClick={props.onClose}
            >
                <FontAwesomeIcon icon={faTimes} />
            </button>

            {props.searchable && (
                <div className={styles.header}>
                    <Search
                        placeholder={props.searchPlaceholder}
                        onSearch={text => setSearchText(text.toLowerCase())}
                    />
                </div>
            )}
            <div
                ref={listRef}
                className={styles.body}
            >
                <OverlayScrollbarsComponent style={scrollStyle}>
                    {props.onSelectAll && listTags.length > 1 && (
                        <AllListItem
                            label={props.selectAllLabel}
                            icon={props.selectAllIcon}
                            onClick={selectAllHandler}
                        />
                    )}
                    {listTags.map(tag => (
                        <ListItem
                            key={String(tag.id)}
                            tag={tag}
                            onClick={() => props.onSelect?.(tag.id)}
                        />
                    ))}
                </OverlayScrollbarsComponent>
            </div>
        </div>,
        document.body
    );
};

export default List;
